import {
  CantileverClassificationFrame,
  classificationSafeVh,
} from '@classification-frame/CantileverClassificationFrame'
import {
  createRootRouteWithContext,
  Outlet,
  retainSearchParams,
  useLocation,
  useMatches,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { SidebarProvider } from 'designSystem/components/Sidebar'
import { GeckoAnalyticsProvider } from 'features/analytics/GeckoAnalyticsProvider'
import { AccessibleMessageApiLayer } from 'features/assetDatabase/contexts/AccessibleMessageApiLayer'
import { GeckoAuthProvider } from 'features/auth/GeckoAuthProvider'
import {
  ChatbotDockHost,
  ChatbotDockProvider,
} from 'features/chatbot/ChatbotDock'
import { DuckDbInstanceProvider } from 'features/duckDB'
import { EmbeddedSidebar } from 'features/embeddedSidebar/EmbeddedSidebar'
import {
  readSidebarOpenFromStorage,
  writeSidebarOpenToStorage,
} from 'features/embeddedSidebar/EmbeddedSidebarContext'
import { EntryBrowserSidebarProvider } from 'features/entryBrowser/sidebar/EntryBrowserSidebarProvider'
import { useHoneycombSdk } from 'features/honeycomb/honeycombSdk'
import { GeckoLocaleProvider } from 'features/locale/GeckoLocaleProvider'
import { PageMaxWidthContext } from 'features/maxPageWidth/MaxPageWidthContextProvider'
import { Navbar } from 'features/navbar/Navbar'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useTrackRecentRoutes } from 'features/navigation/useTrackRecentRoutes'
import { useSelectedOrgAndSiteInterface } from 'features/orgAndSiteSourceOfTruth/orgSiteInterface'
import { useGeckoPermissions } from 'features/permissions'
import { GeckoPermissionsProvider } from 'features/permissions/contexts/GeckoPermissionsProvider'
import { RightDrawerProvider } from 'features/rightDrawer/utils/RightDrawerProvider'
import { useRolodexEntries } from 'features/rolodex/hooks/useRolodexEntries'
import { RolodexProvider } from 'features/rolodex/RolodexProvider'
import { TermsModalProvider } from 'features/terms/TermsOfServiceProvider'
import { DesignSystemThemeRoot } from 'features/theme/DesignSystemThemeProvider'
import { GeckoThemeDocumentSync } from 'features/theme/GeckoThemeDocumentSync'
import { WorkspaceTabBar } from 'features/workspaceTabs/WorkspaceTabBar'
import { WorkspaceTabsProvider } from 'features/workspaceTabs/WorkspaceTabsContext'
import isNil from 'lodash/isNil'
import React, {
  ReactNode,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  globalSearchParamsSchema,
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createVirtualReportsSearch } from 'routes/virtual-reports/-searchParams'
import styled, { css } from 'styled-components'
import {
  GeckoThemeProvider,
  ThemeMode,
  useGeckoThemeModeOverride,
} from 'styles/GeckoTheme'
import type { Cache } from 'swr'
import { PERMISSION_CANTILEVER_VIRTUAL_REPORTS_VIEWER } from 'types'
import { RolodexEntryType } from 'types/rolodex/rolodexEntries'
import { DEFAULT_DOCUMENT_TITLE } from 'utils/documentTitle'

const EntryBrowserSidebar = React.lazy(() =>
  import('features/entryBrowser/sidebar/EntryBrowserSidebar').then((m) => ({
    default: m.EntryBrowserSidebar,
  }))
)

/**
 * Wraps children with a provider that's loaded via dynamic import.
 * Renders children immediately (without the provider context) while the chunk loads,
 * then re-renders with the provider wrapping them once available.
 */
function LazyProvider({
  load,
  children,
}: {
  load: () => Promise<React.ComponentType<{ children: ReactNode }>>
  children: ReactNode
}) {
  const [Provider, setProvider] = useState<React.ComponentType<{
    children: ReactNode
  }> | null>(null)

  useEffect(() => {
    load().then((P) => setProvider(() => P))
  }, [load])

  if (!Provider) return <>{children}</>
  return <Provider>{children}</Provider>
}

const loadDataLayerProvider = () =>
  import('features/dataLayers/DataLayerProvider').then(
    (m) => m.DataLayerProvider
  )

export const Route = createRootRouteWithContext<
  { cache: Cache } & CantileverPageProps
>()({
  component: RootComponent,
  errorComponent: () => <div>There was an error</div>,
  validateSearch: globalSearchParamsSchema,
  search: {
    middlewares: [
      retainSearchParams([SEARCH_PARAM_ORG_ID, SEARCH_PARAM_SITE_ID]),
    ],
  },
})

function RootComponent() {
  const { ENVIRONMENT } = useRuntimeVariables()
  const { pathname } = useLocation()
  const lastMatch = useMatches({ select: (matches) => matches.at(-1) })
  const routeStaticData = lastMatch?.staticData as
    | {
        layout?: Partial<CantileverPageProps>
        skipDefaultStyles?: boolean
      }
    | undefined

  useLayoutEffect(() => {
    document.title = DEFAULT_DOCUMENT_TITLE
  }, [pathname])

  const cantileverPageProps: CantileverPageProps = React.useMemo(() => {
    const layoutFromStatic = routeStaticData?.layout
    const contextFlags =
      (lastMatch?.context as Partial<CantileverPageProps>) ?? {}

    const defaults: CantileverPageProps = {
      enableScrolling: false,
      enforceMaxWidth: false,
      showNavbar: true,
      showSearchBar: true,
      defaultClosedNav: false,
    }
    return { ...defaults, ...contextFlags, ...(layoutFromStatic ?? {}) }
  }, [lastMatch, routeStaticData])

  const coreProviders = (
    <GeckoAuthProvider>
      <GeckoPermissionsProvider>
        <GeckoLocaleProvider>
          <GeckoAnalyticsProvider>
            <RolodexProvider>
              <RootProviderEffects />
              <LazyProvider load={loadDataLayerProvider}>
                <DuckDbInstanceProvider>
                  <TermsModalProvider>
                    <RightDrawerProvider>
                      <AccessibleMessageApiLayer>
                        <EntryBrowserSidebarProvider>
                          <>
                            <CantileverPage {...cantileverPageProps}>
                              <Outlet />
                            </CantileverPage>
                            <Suspense>
                              <EntryBrowserSidebar />
                            </Suspense>
                          </>
                        </EntryBrowserSidebarProvider>
                      </AccessibleMessageApiLayer>
                    </RightDrawerProvider>
                  </TermsModalProvider>
                </DuckDbInstanceProvider>
              </LazyProvider>
            </RolodexProvider>
          </GeckoAnalyticsProvider>
        </GeckoLocaleProvider>
      </GeckoPermissionsProvider>
    </GeckoAuthProvider>
  )

  return (
    <GeckoThemeProvider
      skipDefaultStyles={routeStaticData?.skipDefaultStyles === true}>
      <DesignSystemThemeRoot />
      <GeckoThemeDocumentSync />
      <CantileverClassificationFrame environment={ENVIRONMENT}>
        {coreProviders}
      </CantileverClassificationFrame>
    </GeckoThemeProvider>
  )
}

function RootProviderEffects() {
  useTrackRecentRoutes()
  useEnsureHasSelectedOrganizationWhenHasSelectedSite()
  useRedirectVrOnlyUsers()
  useHoneycombSdk()

  return null
}

const classificationSafeDvh = classificationSafeVh.replace('100vh', '100dvh')

const AppContainer = styled.div<{
  $embeddedSidebar?: boolean
  $useDynamicViewportHeight?: boolean
}>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  flex: 1 1 auto;
  height: ${classificationSafeVh};
  overflow: hidden;

  ${({ $useDynamicViewportHeight }) =>
    $useDynamicViewportHeight &&
    css`
      overscroll-behavior: none;

      @supports (height: 100dvh) {
        height: ${classificationSafeDvh};
      }
    `}

  ${({ $embeddedSidebar, theme }) =>
    $embeddedSidebar &&
    `
    & > div:first-child {
      background-color: ${theme.forecast.pageBackground1};
    }
  `}
`

const PageBg = styled.div<{ $enableScrolling: boolean }>`
  overflow-y: ${({ $enableScrolling }) =>
    $enableScrolling ? 'auto' : 'hidden'};
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`

const ContentContainer = styled.div<{
  $maxWidth?: string
  $enforceMaxWidth?: boolean
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: ${({ $enforceMaxWidth }) => ($enforceMaxWidth ? '0 auto' : '0')};
  max-width: ${({ $maxWidth }) => $maxWidth};
  width: 100%;
`

const SidebarLayoutBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.forecast.background1};
  --sidebar: ${({ theme }) => theme.forecast.pageBackground1};
`

const SidebarMainRow = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  gap: 0.75rem;
`

const ContentCard = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.forecast.pageBackground1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: var(--radius);
  overflow: hidden;
  position: relative;
`

const CardContentScroller = styled.div<{ $enableScrolling: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: ${({ $enableScrolling }) =>
    $enableScrolling ? 'auto' : 'hidden'};
`

type CantileverPageProps = {
  enableScrolling?: boolean
  enforceMaxWidth?: boolean
  showNavbar?: boolean
  showSearchBar?: boolean
  defaultClosedNav?: boolean
  hideSiteSelector?: boolean
  hideMenuButton?: boolean
  useDynamicViewportHeight?: boolean
}

const CantileverPage: React.FC<
  CantileverPageProps & { children: ReactNode }
> = ({
  enableScrolling = false,
  enforceMaxWidth = false,
  showNavbar = true,
  showSearchBar = true,
  defaultClosedNav = false,
  hideSiteSelector = false,
  hideMenuButton = false,
  useDynamicViewportHeight = false,
  children,
}) => {
  const orgOrSiteID = useRouteOrgSiteID()
  const [maxWidth, setMaxWidth] = useState<string>('64rem')
  const { getOrganizationId, getSiteId } = useSelectedOrgAndSiteInterface()

  const orgId = getOrganizationId() ?? ''
  const siteId = getSiteId()

  const { embeddedSidebar } = useNavbarConfig({ orgId, siteId })

  const { hasPermissions } = useGeckoPermissions()
  const isVrOnlyUser = hasPermissions([
    PERMISSION_CANTILEVER_VIRTUAL_REPORTS_VIEWER,
  ])

  const shouldHideSiteSelector = hideSiteSelector || isVrOnlyUser
  const shouldHideSearchBar = !showSearchBar || isVrOnlyUser
  const showEmbeddedSidebar = embeddedSidebar && !isVrOnlyUser && showNavbar
  useGeckoThemeModeOverride(showEmbeddedSidebar ? ThemeMode.Zinc : undefined)

  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpenFromStorage)
  const shouldHideNavbar = !showNavbar || isVrOnlyUser || showEmbeddedSidebar
  const shouldHideMenuButton = hideMenuButton || embeddedSidebar || isVrOnlyUser
  const handleSidebarOpenChange = React.useCallback((open: boolean) => {
    setSidebarOpen(open)
    writeSidebarOpenToStorage(open)
  }, [])
  const contentElement = (
    <ContentContainer
      $maxWidth={enforceMaxWidth ? maxWidth : 'none'}
      $enforceMaxWidth={enforceMaxWidth}>
      {children}
    </ContentContainer>
  )

  return (
    <AppContainer
      $embeddedSidebar={showEmbeddedSidebar}
      $useDynamicViewportHeight={useDynamicViewportHeight}>
      {!shouldHideNavbar && (
        <Navbar
          orgOrSiteID={orgOrSiteID}
          defaultClosed={defaultClosedNav}
          hideSearchBar={shouldHideSearchBar}
          hideSiteSelector={shouldHideSiteSelector}
          hideMenuButton={shouldHideMenuButton}
        />
      )}
      <PageMaxWidthContext.Provider value={{ maxWidth, setMaxWidth }}>
        {showEmbeddedSidebar ? (
          <ChatbotDockProvider>
            <WorkspaceTabsProvider siteId={siteId ?? orgId}>
              <SidebarProvider
                open={sidebarOpen}
                onOpenChange={handleSidebarOpenChange}
                contained
                style={
                  {
                    '--sidebar-width': '260px',
                    '--sidebar-width-icon': '48px',
                  } as React.CSSProperties
                }>
                <SidebarLayoutBody>
                  <SidebarMainRow>
                    <EmbeddedSidebar
                      siteID={siteId}
                      orgID={orgId}
                      orgOrSiteID={orgOrSiteID}
                    />
                    <ContentCard>
                      <WorkspaceTabBar />
                      <CardContentScroller $enableScrolling={enableScrolling}>
                        {contentElement}
                      </CardContentScroller>
                    </ContentCard>
                  </SidebarMainRow>
                  <ChatbotDockHost />
                </SidebarLayoutBody>
              </SidebarProvider>
            </WorkspaceTabsProvider>
          </ChatbotDockProvider>
        ) : (
          <PageBg $enableScrolling={enableScrolling}>{contentElement}</PageBg>
        )}
      </PageMaxWidthContext.Provider>
    </AppContainer>
  )
}

/**
 * Infers the site or org ID from route params.
 * First tries explicit params (orgOrSiteID, siteID, orgID).
 * If none found, looks up all param values in rolodex to infer
 * site/org from lower-level objects (e.g. for details pages to still render site context).
 */
function useRouteOrgSiteID(): string | undefined {
  const params = useParams({ strict: false })
  const { getSiteId, getOrganizationId } = useSelectedOrgAndSiteInterface()

  const knownOrgOrSite =
    params.orgOrSiteID ??
    params.siteID ??
    params.orgID ??
    getSiteId() ??
    getOrganizationId()

  const rolodexFetchProps = useMemo(
    () =>
      Object.entries(params)
        .filter(([, value]) => !isNil(value))
        .map(([, value]) => ({ ids: [value] })),
    [params]
  )

  const { entries } = useRolodexEntries(
    isNil(knownOrgOrSite) ? rolodexFetchProps : undefined
  )

  if (!isNil(knownOrgOrSite)) {
    return knownOrgOrSite
  }

  const siteFromTags = entries
    .map((entry) => entry.tags.site)
    .find((site) => !isNil(site))

  if (!isNil(siteFromTags)) {
    return siteFromTags
  }

  const orgFromTags = entries
    .map((entry) => entry.tags.organization)
    .find((org) => !isNil(org))

  return orgFromTags
}

function useRedirectVrOnlyUsers() {
  const { hasPermissions } = useGeckoPermissions()
  const location = useLocation()
  const navigate = useNavigate()
  const { getOrganizationId, getSiteId } = useSelectedOrgAndSiteInterface()
  const isVrOnlyUser = hasPermissions([
    PERMISSION_CANTILEVER_VIRTUAL_REPORTS_VIEWER,
  ])

  useLayoutEffect(() => {
    if (isVrOnlyUser && !location.pathname.startsWith('/virtual-reports')) {
      void navigate({
        to: '/virtual-reports',
        search: createVirtualReportsSearch({
          orgId: getOrganizationId(),
          siteId: getSiteId(),
        }),
        replace: true,
      })
    }
  }, [getOrganizationId, getSiteId, isVrOnlyUser, location.pathname, navigate])
}

function useEnsureHasSelectedOrganizationWhenHasSelectedSite() {
  const { setOrgAndSiteIds, getSiteId, getOrganizationId } =
    useSelectedOrgAndSiteInterface()

  const selectedSite = getSiteId()
  const { sites } = useRolodexEntries(
    selectedSite ? [{ ids: [selectedSite], limit: 1 }] : undefined
  )

  const siteEntry = sites.at(0)
  const selectedOrg = getOrganizationId()
  useEffect(() => {
    if (isNil(selectedOrg) && !isNil(selectedSite) && siteEntry) {
      setOrgAndSiteIds(
        {
          siteId: siteEntry.id,
          organizationId: siteEntry.tags[RolodexEntryType.ORGANIZATION],
        },
        false
      )
    }
  }, [selectedOrg, selectedSite, setOrgAndSiteIds, siteEntry])
}
